import React, { useState } from 'react';
import { Shield, AlertTriangle, ArrowRight } from 'lucide-react';
import { Modal, Button, Pill, TrustChip } from '../../index';

/**
 * AutonomyOverrideModal — Org Admin-only manual autonomy regression / promotion.
 *
 * The runtime computes autonomy from the trust score by default. This
 * override is the escape hatch for situations where the operator has
 * out-of-band information (e.g., an incident response decision) that
 * the score doesn't yet reflect.
 *
 * UX safety design:
 *   1. The current level is shown prominently with a "from → to" arrow
 *      so the operator can't mis-pick by mistake.
 *   2. Picking a more permissive level surfaces a red banner with the
 *      specific implications.
 *   3. Reason is required and audit-logged with the user identity.
 *   4. Submit is disabled until both fields validate.
 *
 * Phase A3 / G303. Wires to manualAutonomyOverride().
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Object} props.agent
 * @param {Function} props.onSubmit       — async ({ newLevel, reason }) => void
 */
/*
 * The ceilings the platform can actually hold.
 *
 * HUMAN_CONTROLLED was offered here and is not a trust rung at all — it is the
 * SKILL autonomy vocabulary (Skill.java:583, IntentProfile:93).
 * AgentTrustCenterService.mapProfileLevelToTrustBand returns null for it, and
 * manualAutonomyOverride wrote the profile and the MANUAL_OVERRIDE transition
 * row BEFORE that mapping ran — so choosing it turned the Decide gate green,
 * reported a new ceiling on this screen, and left agents.trust_band, the column
 * the runtime enforces on, untouched.
 *
 * The service now refuses an unmappable level outright. This list is the other
 * half: not offering one in the first place.
 */
const AUTONOMY_LEVELS = [
  {
    key: 'SELF_AUTONOMOUS',
    label: 'Self-Autonomous',
    description: 'Runs without human approval; full discretion across all skills.',
    rank: 4,
    tone: 'success',
  },
  {
    key: 'SUPERVISED',
    label: 'Supervised',
    description: 'Autonomous within policy; sensitive actions require human approval.',
    rank: 3,
    tone: 'warning',
  },
  {
    key: 'RESTRICTED',
    label: 'Restricted',
    description: 'Limited skill catalogue; every action surfaces a review prompt.',
    rank: 2,
    tone: 'warning',
  },
  {
    key: 'PROBATION',
    label: 'Probation',
    description: 'All actions blocked pending recovery. Use the Probation modal instead.',
    rank: 0,
    tone: 'danger',
  },
];

export function AutonomyOverrideModal({ isOpen, onClose, agent, onSubmit }) {
  const currentLevel = agent?.autonomy || agent?.autonomyLevel || null;
  const [newLevel, setNewLevel] = useState(currentLevel || 'SUPERVISED');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!agent) return null;

  const currentDef = AUTONOMY_LEVELS.find((l) => l.key === currentLevel);
  const newDef = AUTONOMY_LEVELS.find((l) => l.key === newLevel);
  const isPromotion = currentDef && newDef && newDef.rank > currentDef.rank;
  const isDemotion = currentDef && newDef && newDef.rank < currentDef.rank;
  const isNoChange = currentDef && newDef && newDef.rank === currentDef.rank;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Reason is required for the audit trail.');
      return;
    }
    if (isNoChange) {
      setError('Selected level matches the current level — nothing to override.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit?.({ newLevel, reason: reason.trim() });
      setReason('');
      onClose?.();
    } catch (e) {
      setError(e?.message || 'Failed to apply override.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      dismissOnBackdrop={!submitting}
      size="md"
      title={
        <span className="inline-flex items-center gap-2">
          <Shield size={18} className="text-v2-action-strong" />
          Override autonomy level
        </span>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            variant={isPromotion ? 'danger' : 'primary'}
            onClick={handleSubmit}
            disabled={submitting || !reason.trim() || isNoChange}
          >
            {submitting ? 'Applying…' : isPromotion ? 'Apply promotion' : 'Apply override'}
          </Button>
        </>
      }
    >
      {/* Agent context */}
      <div className="bg-v2-surface-muted rounded-v2-md p-3 mb-4 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-v2-ink text-[14px] truncate">{agent.name}</div>
          <div className="text-[11px] text-v2-ink-muted truncate">ID {String(agent.id).slice(0, 8)}…</div>
        </div>
        {/* Canonical complianceScore (null → Unrated), not the trustScore baseline. */}
        <TrustChip score={agent.complianceScore} autonomy={currentLevel} size="sm" />
      </div>

      {/* From → To preview */}
      <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-3 mb-4">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-2">Change</div>
        <div className="flex items-center gap-2">
          <Pill tone={currentDef?.tone || 'muted'} size="sm">{currentDef?.label || currentLevel || 'Unset'}</Pill>
          <ArrowRight size={14} className="text-v2-ink-muted shrink-0" />
          <Pill tone={newDef?.tone || 'muted'} size="sm" dot={isPromotion || isDemotion}>{newDef?.label || newLevel}</Pill>
          {isPromotion && <span className="text-[11px] font-semibold text-v2-danger-strong ml-auto">⚠ Promotion — increases autonomy</span>}
          {isDemotion && <span className="text-[11px] font-semibold text-v2-warning-strong ml-auto">↓ Demotion</span>}
          {isNoChange && <span className="text-[11px] text-v2-ink-muted ml-auto">No change</span>}
        </div>
      </div>

      {/* Level picker */}
      <fieldset className="mb-4">
        <legend className="block text-[11px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-2">
          New autonomy level
        </legend>
        <div className="space-y-1.5">
          {AUTONOMY_LEVELS.map((lvl) => {
            const selected = newLevel === lvl.key;
            return (
              <label key={lvl.key}
                     className={`flex items-start gap-3 p-3 rounded-v2-sm border cursor-pointer transition-colors ${
                       selected
                         ? 'border-v2-action bg-v2-action-soft'
                         : 'border-v2-border-subtle hover:bg-v2-surface-muted'
                     }`}>
                <input
                  type="radio"
                  name="autonomy"
                  value={lvl.key}
                  checked={selected}
                  onChange={() => setNewLevel(lvl.key)}
                  disabled={submitting}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-v2-ink text-[13px]">{lvl.label}</span>
                    {currentLevel === lvl.key && (
                      <Pill tone="muted" size="sm">Current</Pill>
                    )}
                  </div>
                  <div className="text-[11.5px] text-v2-ink-secondary mt-0.5">{lvl.description}</div>
                </div>
              </label>
            );
          })}
        </div>
      </fieldset>

      {/* Promotion warning */}
      {isPromotion && (
        <div className="bg-v2-danger-soft border border-v2-danger/30 rounded-v2-sm px-3 py-2 text-[12px] text-v2-danger-strong mb-3 flex items-start gap-2">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <div>
            <strong>You are overriding the runtime's risk decision.</strong> The agent's trust
            score does not currently justify this autonomy level. Promotion may expose the
            organisation to violations until the score catches up.
          </div>
        </div>
      )}

      {/* Reason */}
      <label className="block mb-2">
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
          Reason <span className="text-v2-danger-strong">*</span>
        </span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Incident IR-2401 closed; agent passed post-mortem review. Promoting back to Supervised."
          rows={3}
          className="w-full border border-v2-border-subtle rounded-v2-sm px-3 py-2 text-[13px] text-v2-ink bg-v2-surface focus:border-v2-action focus:ring-2 focus:ring-v2-action/20 focus:outline-none resize-y"
          disabled={submitting}
        />
        <span className="text-[10.5px] text-v2-ink-muted mt-1 block">
          Audit-logged with your user ID. Org admins can review this on the Audit Trail page.
        </span>
      </label>

      {error && (
        <div className="mt-3 bg-v2-danger-soft border border-v2-danger/30 rounded-v2-sm px-3 py-2 text-[12px] text-v2-danger-strong">
          {error}
        </div>
      )}
    </Modal>
  );
}

export default AutonomyOverrideModal;
